import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Alert,
  AlertIcon,
  Text,
  useColorModeValue,
  useBreakpointValue,
  SimpleGrid,
  VisuallyHidden,
} from '@chakra-ui/react'
import { useState, useMemo, useEffect } from 'react'
import { FiChevronUp, FiChevronDown } from 'react-icons/fi'
import { useTranslation } from 'react-i18next'
import { DataTableProps, SortConfig, ColumnConfig } from './types'
import { DataTableFilters } from './components/DataTableFilters'
import { DataTablePagination } from './components/DataTablePagination'
import { DataTableActions } from './components/DataTableActions'
import { DataTableMobileCard } from './components/DataTableMobileCard'
import { TableSkeleton } from '@/components/common/LoadingSkeleton'

export function DataTable<T = any>({
  data,
  loading = false,
  error = null,
  columns,
  filters = [],
  initialFilters,
  onFiltersChange,
  showFilterBar = true,
  persistOption,
  filterLayout = 'default',
  sortable = true,
  defaultSort,
  onSortChange,
  pagination,
  onPageChange,
  onPageSizeChange,
  actions,
  actionsColumnWidth = '10%',
  size = 'sm',
  emptyMessage,
  onRowClick,
  showRefresh = false,
  onRefresh,
  refreshing = false,
  showExport = false,
  onExport,
  mobileBreakpoint = 'md',
  mobileCardRender,
}: DataTableProps<T>) {
  const { t } = useTranslation()
  const [currentSort, setCurrentSort] = useState<SortConfig | null>(defaultSort || null)
  const [filterValues, setFilterValues] = useState<Record<string, any>>(initialFilters || {})

  useEffect(() => {
    if (initialFilters) {
      setFilterValues(initialFilters)
    } else if (!initialFilters && Object.keys(filterValues).length > 0) {
      setFilterValues({})
    }
  }, [initialFilters])

  // Color mode values
  const cardBg = useColorModeValue('white', 'gray.700')
  const borderColor = useColorModeValue('gray.200', 'gray.600')
  const textColor = useColorModeValue('gray.600', 'gray.300')
  const rowHoverBg = useColorModeValue('gray.50', 'gray.600')

  // Responsive breakpoint
  const isMobile = useBreakpointValue(
    { base: true, [mobileBreakpoint]: false },
    { fallback: 'md' }
  )

  // Handle sorting
  const handleSort = (columnKey: string) => {
    if (!sortable) return

    let newSort: SortConfig | null = null

    if (!currentSort || currentSort.key !== columnKey) {
      newSort = { key: columnKey, direction: 'asc' }
    } else if (currentSort.direction === 'asc') {
      newSort = { key: columnKey, direction: 'desc' }
    } else {
      newSort = null // Clear sort
    }

    setCurrentSort(newSort)
    onSortChange?.(newSort)
  }

  // Handle filter changes
  const handleFiltersChange = (newFilters: Record<string, any>) => {
    setFilterValues(newFilters)
    onFiltersChange?.(newFilters)
  }

  // Clear filters
  const handleClearFilters = () => {
    setFilterValues({})
    onFiltersChange?.({})
  }

  // Get column with actions if needed
  const allColumns = useMemo(() => {
    const cols: ColumnConfig<T>[] = [...columns]
    if (actions) {
      cols.push({
        key: '__actions__' as keyof T,
        title: t('common.actions'),
        width: actionsColumnWidth,
        sortable: false,
      })
    }
    return cols
  }, [columns, actions, actionsColumnWidth, t])

  // Get the value from a row using the column key
  const getValue = (row: T, key: keyof T | string): any => {
    if (typeof key === 'string' && key.includes('.')) {
      // Handle nested keys like 'user.name'
      return key.split('.').reduce((obj: any, k: string) => obj?.[k], row)
    }
    return (row as any)[key]
  }

  // Render sort icon
  const renderSortIcon = (columnKey: string) => {
    if (!sortable) return null
    if (!currentSort || currentSort.key !== columnKey) return null
    
    return currentSort.direction === 'asc' ? <FiChevronUp /> : <FiChevronDown />
  }

  // Loading state
  if (loading) {
    return (
      <Box role="status" aria-busy="true" py={4}>
        <TableSkeleton rows={6} />
        <VisuallyHidden>{t('common.loading')}</VisuallyHidden>
      </Box>
    )
  }

  // Error state
  if (error) {
    return (
      <Alert status="error">
        <AlertIcon />
        {t('common.error')} - {error.message}
      </Alert>
    )
  }

  return (
    <Box display="flex" flexDirection="column">
      {/* Filters - Fixed at top */}
      {showFilterBar && filters.length > 0 && (
        <Box flexShrink={0} mb={4}>
          <DataTableFilters
            filters={filters}
            values={filterValues}
            onChange={handleFiltersChange}
            onClear={handleClearFilters}
            showExport={showExport}
            onExport={onExport}
            showRefresh={showRefresh}
            onRefresh={onRefresh}
            refreshing={refreshing}
            persistOption={persistOption}
            layout={filterLayout}
          />
        </Box>
      )}

      {/* Table Container - Auto sized */}
      <Box 
        bg={cardBg} 
        borderRadius="lg" 
        border="1px" 
        borderColor={borderColor} 
        overflow="hidden"
        display="flex"
        flexDirection="column"
      >
        {data && data.length > 0 ? (
          <>
            {/* Table Scrollable Area - Auto sized */}
            <Box overflow="auto">
              {/* Desktop Table View */}
              {!isMobile && (
                <Table variant="simple" size={size}>
                  <Thead>
                    <Tr>
                      {allColumns.map((column) => (
                        <Th
                          key={String(column.key)}
                          width={column.width}
                          textAlign={column.align || 'left'}
                          cursor={column.sortable !== false && sortable ? 'pointer' : 'default'}
                          onClick={() => column.sortable !== false && handleSort(String(column.key))}
                          whiteSpace={column.noWrap ? 'nowrap' : 'normal'}
                        >
                          <Box display="flex" alignItems="center" gap={1}>
                            {column.title}
                            {renderSortIcon(String(column.key))}
                          </Box>
                        </Th>
                      ))}
                    </Tr>
                  </Thead>
                  <Tbody>
                    {data.map((row, rowIndex) => (
                      <Tr
                        key={rowIndex}
                        cursor={onRowClick ? 'pointer' : 'default'}
                        onClick={() => onRowClick?.(row)}
                        _hover={onRowClick ? { bg: rowHoverBg } : {}}
                      >
                        {allColumns.map((column) => {
                          if (String(column.key) === '__actions__') {
                            const rowActions = actions?.(row) || []
                            return (
                              <Td key={String(column.key)} textAlign={column.align || 'left'}>
                                <DataTableActions row={row} actions={rowActions} />
                              </Td>
                            )
                          }

                          const value = getValue(row, column.key)
                          return (
                            <Td
                              key={String(column.key)}
                              textAlign={column.align || 'left'}
                              whiteSpace={column.noWrap ? 'nowrap' : 'normal'}
                            >
                              <Box>
                                {column.render ? column.render(value, row) : String(value || '')}
                                {column.renderSubText && (
                                  <Text fontSize="xs" color={textColor}>
                                    {column.renderSubText(row)}
                                  </Text>
                                )}
                              </Box>
                            </Td>
                          )
                        })}
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              )}

              {/* Mobile Card View */}
              {isMobile && (
                <Box p={4}>
                  <SimpleGrid columns={1} spacing={4}>
                    {data.map((row, index) => (
                      <DataTableMobileCard
                        key={index}
                        row={row}
                        columns={columns}
                        actions={actions?.(row)}
                        onRowClick={onRowClick}
                        customRender={mobileCardRender}
                      />
                    ))}
                  </SimpleGrid>
                </Box>
              )}
            </Box>

            {/* Pagination - Fixed at bottom */}
            {pagination && pagination.total > 0 && onPageChange && onPageSizeChange && (
              <Box flexShrink={0} borderTop="1px" borderColor={borderColor}>
                <DataTablePagination
                  pagination={pagination}
                  onPageChange={onPageChange}
                  onPageSizeChange={onPageSizeChange}
                />
              </Box>
            )}
          </>
        ) : (
          // Empty state - Centered in available space
          <Box flex="1" display="flex" alignItems="center" justifyContent="center">
            <Box textAlign="center" p={8}>
              <Text color="gray.500" fontSize="lg">
                {emptyMessage || t('common.noDataFound')}
              </Text>
              <Text color="gray.400" fontSize="sm" mt={2}>
                {t('common.dataWillAppear')}
              </Text>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  )
}
